import { Autocomplete, TextField } from "@mui/material";
import classNames from "classnames";
import lodash from "lodash";
import * as React from "react";

import {
  CAMERA_TYPE,
  type Entity,
  type OrbitCamera,
} from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./SceneSettings.module.css";

/** What the camera follows, as the target object itself carries it. */
type TrackedTarget = OrbitCamera["target"];

/**
 * The qualified path a target reads as: the entity, the component on it, and —
 * when one is named — the node inside the model that component draws.
 *
 * **Derived, never stored.** The prefix is `id` and `mesh`, which the target
 * already holds; storing the joined string as well would let the two disagree
 * the moment `RenameThreeDEntity` re-aims the camera, and a stale prefix reads
 * as a target that no longer exists. It is also why the picker below is built on
 * option *objects* rather than strings: a glTF node name may contain dots, so a
 * joined path cannot be split back apart without knowing where the component
 * ends. Same reason a topic and its message path are kept apart in this schema.
 *
 * Joining is therefore one-way, and one-way is all that is needed: the control's
 * value and its options are both targets, so nothing ever parses a path.
 */
function qualifiedPath(target: TrackedTarget): string {
  const component = `${target.id}.${target.mesh}`;
  return target.node === undefined ? component : `${component}.${target.node}`;
}

/** Whether two targets name the same thing. */
function sameTarget(a: TrackedTarget, b: TrackedTarget): boolean {
  return a.id === b.id && a.mesh === b.mesh && a.node === b.node;
}

/**
 * The node names the model on this entity declares, in the order a person would
 * look for them.
 *
 * Read from the entity's own `mesh.joints`, which an import copies out of the
 * file — so this needs no fetch, works for a saved workspace whose model file
 * has moved, and offers real names rather than free text. `Joint.node` is the
 * joint's **child link**, which is exactly the set of parts that move: a
 * gripper, a wrist, a head. A link nothing articulates is rigidly fixed to its
 * parent and following it is following its parent, so its absence costs nothing
 * a user would notice.
 */
function trackableNodes(entity: Entity | undefined): string[] {
  if (entity?.mesh?.type !== "model") {
    return [];
  }
  const names = (entity.mesh.joints ?? []).map((joint) => joint.node);
  return lodash.uniq(names).sort();
}

/**
 * Every target the camera may be pointed at, across the whole panel.
 *
 * One flat list rather than an entity picker feeding a node picker, because
 * choosing what to follow is one decision and the qualified names already make
 * it unambiguous — a panel with two robots offers `entity0.mesh.gripper_link`
 * and `entity1.mesh.gripper_link` as separate entries, which is what the
 * qualified naming was for.
 *
 * Each entity's own components come first, with that entity's nodes directly
 * beneath the `mesh` they belong to, so the list reads as an outline: the whole
 * robot, then its parts, then its trace and its point cloud. The bare
 * `entity0.mesh` entry is what makes "follow the robot itself" both the default
 * and the way back from having chosen a part. A cloud is offered under its own
 * name because a panel opened from a point cloud topic draws nothing else, and
 * following the cloud is following its sensor.
 */
function targetOptions(entities: Record<string, Entity>): TrackedTarget[] {
  return Object.keys(entities).flatMap((id) => [
    ...(entities[id].mesh
      ? [
          { id, mesh: "mesh" as const },
          ...trackableNodes(entities[id]).map((node) => ({
            id,
            mesh: "mesh" as const,
            node,
          })),
        ]
      : []),
    ...(entities[id].path ? [{ id, mesh: "path" as const }] : []),
    ...(entities[id].pointCloud ? [{ id, mesh: "pointCloud" as const }] : []),
  ]);
}

interface OrbitCameraFieldsProps {
  /** The orbit camera as it currently stands. */
  value: OrbitCamera;
  /**
   * The entities the camera may track, keyed by id — the `mesh`, `path` and
   * `pointCloud` components on each are what make an entity a legal target,
   * and a `mesh` drawing a model contributes its nodes as targets of their own.
   */
  entities: Record<string, Entity>;
  /**
   * Called with the edited camera whenever the inputs parse to something
   * different from {@link OrbitCameraFieldsProps.value}.
   *
   * **Absent means read-only**, and read-only here also means *unvalidated*: a
   * caller showing an orbit spec it will not write back — the panel, when its
   * camera is actually third-person and this is displaying a placeholder — must
   * not decorate those placeholder values with errors about entities that do not
   * exist.
   *
   * Should otherwise be stable across renders; see {@link GridFields}.
   */
  onChange?: (camera: OrbitCamera) => void;
}

/**
 * The orbit camera's target and zoom limits, as plain inputs.
 *
 * Presentational — see {@link GridFields} for why the store binding is a
 * separate component. Orbit **only**: the fields here (a target, a zoom
 * envelope) do not exist on a third-person camera, which frames a free-floating
 * point instead; see {@link ThirdPersonCameraFields} for that shape. Writing one
 * shape's fields into the other is exactly the mistake the discriminated union
 * exists to prevent, so neither component accepts the other's spec.
 *
 * ## One control for a target that is stored as three fields
 *
 * The camera may follow an entity, or one node inside the model that entity
 * draws. Panel state keeps those apart — `{id, mesh, node?}` — because the
 * qualified `entity0.mesh.gripper_link` path cannot be split back apart when a
 * glTF node name contains a dot, and because a stored prefix would go stale the
 * moment `RenameThreeDEntity` re-aims the camera.
 *
 * None of that surfaces here. Picking a target is one decision, so it is one
 * control over one list, and a choice writes all three fields in a single
 * change. Splitting it into an entity field and a node field would leave a
 * moment where the entity had changed and the node still named a link from the
 * previous model — a target that resolves to nothing, which the renderer
 * silently falls back to the entity for.
 */
export function OrbitCameraFields({
  value,
  entities,
  onChange,
}: OrbitCameraFieldsProps) {
  const [target, setTarget] = React.useState<TrackedTarget>(value.target);
  const [minZoom, setMinZoom] = React.useState(`${value.zoom?.min ?? ""}`);
  const [maxZoom, setMaxZoom] = React.useState(`${value.zoom?.max ?? ""}`);

  const [targetError, setTargetError] = React.useState("");
  const [minZoomError, setMinZoomError] = React.useState("");
  const [maxZoomError, setMaxZoomError] = React.useState("");

  React.useEffect(
    function onSettingChanged() {
      if (!onChange) {
        return;
      }
      setTargetError("");
      if (!entities[target.id] || !entities[target.id][target.mesh]) {
        setTargetError(`Component ${target.id}.${target.mesh} does not exist`);
        return;
      }
      setMinZoomError("");
      const parsedMinZoom =
        minZoom === "" ? undefined : Number.parseFloat(minZoom);
      if (parsedMinZoom === undefined) {
        // noop
      } else if (!Number.isFinite(parsedMinZoom)) {
        setMinZoomError("Zoom limits must be numbers");
        return;
      } else if (parsedMinZoom < 0) {
        setMinZoomError("Zoom limits must not be negative");
        return;
      }
      setMaxZoomError("");
      const parsedMaxZoom =
        maxZoom === "" ? undefined : Number.parseFloat(maxZoom);
      if (parsedMaxZoom === undefined) {
        // Noop
      } else if (!Number.isFinite(parsedMaxZoom)) {
        setMaxZoomError("Zoom limits must be numbers");
        return;
      } else if (parsedMaxZoom < 0) {
        setMaxZoomError("Zoom limits must not be negative");
        return;
      } else if (parsedMinZoom !== undefined && parsedMaxZoom < parsedMinZoom) {
        setMaxZoomError("Max zoom must be greater than min zoom");
        return;
      }

      const zoom = {
        ...(parsedMinZoom !== undefined && { min: parsedMinZoom }),
        ...(parsedMaxZoom !== undefined && { max: parsedMaxZoom }),
      };

      const updatedCamera: OrbitCamera = {
        type: CAMERA_TYPE.orbit,
        // Spread rather than rebuilt field by field, so `node` is carried
        // through when set and stays absent when not — writing `node: undefined`
        // would compare unequal to a camera that never had one and fire this on
        // every render. All three fields move together, in this one write.
        target: { ...target },
        zoom,
      };

      if (!lodash.isEqual(value, updatedCamera)) {
        onChange(updatedCamera);
      }
    },
    [target, minZoom, maxZoom, entities, value, onChange],
  );

  const options = targetOptions(entities);
  // The stored target, when the panel no longer offers it: an entity that has
  // been deleted, or a node the model stopped declaring after a re-pick or a
  // re-ingest with a link renamed. It still has to be *shown* — a control that
  // silently displayed something else would be lying about what the camera is
  // doing — so it joins its own option list, and says what is wrong below.
  const missing = !options.some((option) => sameTarget(option, target));
  const nodeUnknown = missing && target.node !== undefined;

  return (
    <div className={styles.container}>
      <Autocomplete
        size="small"
        fullWidth
        options={missing ? [target, ...options] : options}
        getOptionLabel={qualifiedPath}
        isOptionEqualToValue={sameTarget}
        // One change, carrying whichever of the three fields the chosen entry
        // names. Selecting the bare `entity.mesh` entry is how a user goes back
        // to following the robot rather than one of its parts, and it clears the
        // stored node by simply not carrying one.
        onChange={(_, next) => !!next && setTarget(next)}
        value={target}
        className={`${styles.input} ${styles.field}`}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Camera Target"
            InputProps={{
              ...params.InputProps,
              className: styles.input,
            }}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true,
              className: styles.input,
            }}
            error={!!targetError}
            helperText={
              targetError ||
              (nodeUnknown
                ? `This model has no node named ${target.node} — the camera follows ${target.id}`
                : "")
            }
          />
        )}
      />
      <div className={`${styles.inputContainer} ${styles.field}`}>
        <TextField
          label="Min Zoom"
          autoComplete="off"
          type="number"
          size="small"
          value={minZoom}
          onChange={(e) => setMinZoom(e.target.value)}
          error={!!minZoomError}
          helperText={minZoomError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: classNames(styles.input, styles.field) }}
        />
        <div className={styles.spacer} />
        <TextField
          label="Max Zoom"
          autoComplete="off"
          type="number"
          size="small"
          value={maxZoom}
          onChange={(e) => setMaxZoom(e.target.value)}
          error={!!maxZoomError}
          helperText={maxZoomError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: classNames(styles.input, styles.field) }}
        />
      </div>
    </div>
  );
}
